/**
 * Role icon uploads: any size is accepted (owner decision: images of any
 * size are shrunk, never refused). The shared image pipeline (lib/image,
 * purpose `icon`) decodes and shrinks the picked file first, in its worker,
 * so a 100 MB, 100 MP photo works as it does for avatars; that result is
 * drawn into a 64x64 square (ROLE_ICON_SIZE) and encoded as WebP (PNG where
 * the browser can't write WebP), so what goes to the server is a few KB.
 * The server still sniffs the type and caps the size at 256 KB.
 */
import { ROLE_ICON_MAX_BYTES, ROLE_ICON_SIZE } from '@neosocial/protocol';
import { ImageError, isAbort, looksLikeImage, optimizeImage } from './image';

export type RoleIconError = 'not-image' | 'decode' | 'too-big';

export class RoleIconProblem extends Error {
  constructor(readonly reason: RoleIconError) {
    super(`role icon: ${reason}`);
  }
}

/** Any size in: the image pipeline's icon shrink (at most 512 x 512), as a Blob. */
async function viaPipeline(file: Blob): Promise<Blob> {
  try {
    return (await optimizeImage(file, 'icon')).blob;
  } catch (e) {
    if (isAbort(e)) throw e;
    // not an image the browser can read (HEIC, a renamed file...)
    if (e instanceof ImageError && (e.kind === 'unsupported' || e.kind === 'heic'))
      throw new RoleIconProblem('not-image');
    throw new RoleIconProblem('decode');
  }
}

/**
 * Where to draw a `w` x `h` image inside a `size` square so all of it shows
 * (contain), centred, on whole pixels so the edges stay crisp.
 */
export function containRect(
  w: number,
  h: number,
  size: number,
): { x: number; y: number; w: number; h: number } {
  if (w <= 0 || h <= 0) return { x: 0, y: 0, w: size, h: size };
  const k = Math.min(size / w, size / h);
  const dw = Math.max(1, Math.round(w * k));
  const dh = Math.max(1, Math.round(h * k));
  return { x: Math.floor((size - dw) / 2), y: Math.floor((size - dh) / 2), w: dw, h: dh };
}

/** Bytes of a base64 data URI's payload. */
export function dataUriBytes(uri: string): number {
  const i = uri.indexOf(',');
  const b64 = i >= 0 ? uri.slice(i + 1) : uri;
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - pad;
}

async function decode(file: Blob): Promise<CanvasImageSource & { width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall back to an <img> below */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return Object.assign(img, { width: img.naturalWidth, height: img.naturalHeight });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Shrinks an image file of any size to a `size` x `size` role icon and
 * returns it as a data URI (WebP, or PNG as a fallback): through the image
 * pipeline first, then into the square. Throws `RoleIconProblem` for a
 * file that isn't an image, can't be decoded, or still comes out too big.
 */
export async function shrinkRoleIcon(file: Blob, size = ROLE_ICON_SIZE): Promise<string> {
  const image =
    file instanceof File ? looksLikeImage(file) : file.type === '' || /^image\//.test(file.type);
  if (!image) throw new RoleIconProblem('not-image');
  const small = await viaPipeline(file);
  let img: Awaited<ReturnType<typeof decode>>;
  try {
    img = await decode(small);
  } catch {
    throw new RoleIconProblem('decode');
  }
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new RoleIconProblem('decode');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const r = containRect(img.width, img.height, size);
  ctx.drawImage(img, r.x, r.y, r.w, r.h);
  if ('close' in img && typeof img.close === 'function') img.close();
  let uri = canvas.toDataURL('image/webp', 0.92);
  if (!uri.startsWith('data:image/webp')) uri = canvas.toDataURL('image/png');
  if (dataUriBytes(uri) > ROLE_ICON_MAX_BYTES) throw new RoleIconProblem('too-big');
  return uri;
}

/** Reads an existing role icon (to duplicate a role) back into a data URI. */
export async function roleIconAsDataUri(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(fr.error);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
